import api from 'Cloud/Application/api';
import { extInfo } from 'lib/extInfo';
import { CAMERA_UPLOADS_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { FeedItemApiResponse } from 'reactApp/modules/feed/feed.types';
import {
    changeGalleryCategory,
    galleryLoadMoreRequest,
    galleryLoadMoreSuccess,
    galleryLoadRequest,
    galleryLoadStart,
    galleryLoadSuccess,
} from 'reactApp/modules/gallery/gallery.module';
import { routeChangeStart } from 'reactApp/modules/router/router.module';
import { getLoadMoreLimit } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { call, cancel, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { getGalleryCategory, getGalleryCursorByCategory, isGalleryCategoryLoaded } from './gallery.selectors';
import { GalleryCategoryType } from './gallery.types';

const LOAD_LIMIT = getLoadMoreLimit(EStorageType.gallery);

const apiCall = ({
    limit = LOAD_LIMIT,
    cursor,
    extensions,
    include,
    exclude,
}: {
    limit?: number;
    cursor?: string;
    extensions?: string;
    include?: string[];
    exclude?: string[];
}): Promise<FeedItemApiResponse> =>
    Promise.resolve(
        api.feed({
            exts: extensions,
            cursor,
            limit,
            include,
            exclude,
        })
    );

function* fetchGallery({ category = null, resetCursor = false }: { category: GalleryCategoryType | null; resetCursor?: boolean }) {
    try {
        if (!category) {
            category = yield select(getGalleryCategory);
        }
        const cursor = resetCursor ? '' : yield select(getGalleryCursorByCategory, category as GalleryCategoryType);
        const extensions = extInfo.getExtsForGallery()?.join(',');

        const data: { extensions: string; cursor: string; include?: string[]; exclude?: string[] } = { extensions, cursor };

        if (category === GalleryCategoryType.mobile) {
            data.include = [CAMERA_UPLOADS_FOLDER_ID];
        } else if (category === GalleryCategoryType.desktop) {
            data.exclude = [CAMERA_UPLOADS_FOLDER_ID];
        }

        return yield call(apiCall, data);
    } catch (error) {
        yield cancel();
    }
}

function* loadGalleryByRequest(action) {
    const category = action?.payload?.category || GalleryCategoryType.all;
    yield put(galleryLoadStart({ category }));

    const data = yield fetchGallery({ category, resetCursor: true });
    yield put(galleryLoadSuccess({ category, data }));
}

function* loadGalleryOnStart(action) {
    const storage = action.payload.storage;

    const isLoaded = yield select(isGalleryCategoryLoaded, GalleryCategoryType.all);

    if (storage !== EStorageType.gallery || (storage === EStorageType.gallery && isLoaded)) {
        return;
    }

    yield loadGalleryByRequest({ category: GalleryCategoryType.all });
}

export function* loadGalleryCategory(action) {
    const category = action.payload.category;

    yield put(galleryLoadStart({ category }));

    const data = yield fetchGallery({ category });
    yield put(galleryLoadSuccess({ category, data }));
}

function* loadMoreGallery(action) {
    const category = action?.payload?.category;
    const data = yield fetchGallery({ category });
    yield put(galleryLoadMoreSuccess({ data, category }));
}

export function* watchGallery() {
    yield takeEvery(routeChangeStart.toString(), loadGalleryOnStart);
    yield takeEvery(galleryLoadRequest.toString(), loadGalleryByRequest);
    yield takeEvery(changeGalleryCategory.toString(), loadGalleryCategory);
    yield takeLatest(galleryLoadMoreRequest.toString(), loadMoreGallery);
}
